import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {DeleteSavedViewVariables} from '../components/views/types'
import {LABELS} from '../constants/labels'
import {parseFetchErrorMessage, SAVED_VIEW_QUERY_KEY, SAVED_VIEWS_QUERY_KEY} from '../queries/saved-views'

interface UseDeleteSavedViewMutationOptions {
  owner: string
  repo: string
}

// Drops the single-view cache for the deleted id, then invalidates the list.
export function useDeleteSavedViewMutation({owner, repo}: UseDeleteSavedViewMutationOptions) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteSavedViewVariables>({
    mutationFn: async ({id}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views/${id}.json`, {
        method: 'DELETE',
      })
      if (!response.ok) {
        throw new Error(await parseFetchErrorMessage(response, LABELS.views.deleteError))
      }
    },
    onSuccess: (_data, {id}) => {
      queryClient.removeQueries({queryKey: [SAVED_VIEW_QUERY_KEY, owner, repo, String(id)] as const})
      void queryClient.invalidateQueries({queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo]})
    },
    onError: () => announce(LABELS.views.deleteError, {assertive: true}),
  })
}
